/** Apartment-Reinigung: Schlafplatz-Maxima und der Fotonachweis (Raumliste, Fotos verlangen). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, Eyebrow } from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';
import { propertyKeys } from '../queries';
import { CleaningCapacityCard } from './CleaningCapacityCard';
import { CleaningEvidenceCard } from './CleaningEvidenceCard';
import { useApartmentDetail } from './ApartmentDetailLayout';

export function ReinigungTab() {
  const { t } = useTranslation();
  const { propertyId, readOnly } = useApartmentDetail();
  const api = useApi();
  const queryClient = useQueryClient();
  const propertyQuery = useQuery({
    queryKey: propertyKeys.detail(propertyId),
    queryFn: () => api.properties.get(propertyId),
  });
  const update = useMutation({
    mutationFn: (body: { availableBedCount: number; availableSofaBedCount: number }) =>
      api.properties.updateCleaningCapacity(propertyId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) });
      toast.success(t('admin.cleaningCapacity.saved'));
    },
    onError: async (error) => {
      const apiError = await toApiError(error);
      toast.error(apiError.detail ?? apiError.title);
    },
  });

  return (
    <>
      <header className="mb-6">
        <Eyebrow>Apartment</Eyebrow>
        <h1 className="text-ink mt-2 font-serif text-3xl tracking-tight">Reinigung</h1>
        <p className="text-ink-soft mt-1.5 text-sm">
          Die Vorlage, aus der nach jeder Abreise automatisch eine Reinigungsaufgabe entsteht.
        </p>
      </header>

      {propertyQuery.isLoading && (
        <Card className="mb-6">
          <CardBody className="text-ink-soft p-6 text-sm" role="status">
            {t('admin.cleaningCapacity.loading')}
          </CardBody>
        </Card>
      )}
      {propertyQuery.isError && (
        <Card className="mb-6">
          <CardBody className="p-6">
            {/* `text-danger` gibt es in keiner Tokenquelle — der Fehlertext stand hier in der
                normalen Textfarbe und war als Fehler nicht zu erkennen. */}
            <p className="text-sealwax text-sm">{t('admin.cleaningCapacity.error')}</p>
            <Button className="mt-4" variant="ghost" onClick={() => void propertyQuery.refetch()}>
              {t('admin.cleaningCapacity.retry')}
            </Button>
          </CardBody>
        </Card>
      )}
      {propertyQuery.data && (
        <div className="mb-6">
          <CleaningCapacityCard
            key={propertyId}
            availableBedCount={propertyQuery.data.availableBedCount}
            availableSofaBedCount={propertyQuery.data.availableSofaBedCount}
            saving={update.isPending}
            readOnly={readOnly}
            onSave={async (body) => {
              await update.mutateAsync(body);
            }}
          />
        </div>
      )}

      <CleaningEvidenceCard propertyId={propertyId} />
    </>
  );
}
